import React from 'react';
import { ArrowRight, Star } from 'lucide-react';
import { EMAIL_DESIGNS, KLAVIYO_BADGE, TRUSTPILOT } from '../mock';

const TP_GREEN = '#00b67a';

function HeroTrustStars({ value = 5, size = 15 }) {
  return (
    <span className="flex items-center gap-0.5" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, value - i));
        return (
          <span
            key={i}
            className="relative inline-flex items-center justify-center rounded-[2px]"
            style={{ width: size, height: size, backgroundColor: '#dcdce6' }}
          >
            <span
              className="absolute inset-y-0 left-0 overflow-hidden rounded-l-[2px]"
              style={{ width: `${fill * 100}%`, backgroundColor: TP_GREEN }}
            />
            <Star size={size * 0.62} className="relative" style={{ color: '#fff', fill: '#fff' }} strokeWidth={0} />
          </span>
        );
      })}
    </span>
  );
}

function EmailColumn({ items, direction, duration }) {
  const loop = [...items, ...items];
  return (
    <div className="h-full overflow-hidden">
      <div
        className={`ob-vtrack ${direction === 'down' ? 'ob-vtrack--down' : 'ob-vtrack--up'}`}
        style={{ animationDuration: `${duration}s` }}
      >
        {loop.map((e, i) => (
          <div key={`${e.id}-${i}`} className="ob-email-card overflow-hidden rounded-2xl bg-white">
            <img src={e.src} alt={e.alt} loading="lazy" className="block w-full" draggable={false} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Hero() {
  const d = EMAIL_DESIGNS;
  const colA = [d[0], d[3], d[1], d[5]];
  const colB = [d[2], d[4], d[6], d[0]];
  const colC = [d[5], d[1], d[6], d[3]];

  return (
    <section id="top" className="relative overflow-hidden pt-28 md:pt-0">
      <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-10 px-5 md:min-h-screen md:grid-cols-[1fr_1.25fr] md:gap-6 md:px-8">
        {/* Left */}
        <div className="relative z-10 py-8 md:py-24">
          {/* Official Klaviyo Partner badge (supplied artwork) */}
          <div className="mb-7">
            <img
              src={KLAVIYO_BADGE}
              alt="Official Klaviyo Partner"
              className="h-14 w-auto sm:h-16"
              draggable={false}
            />
          </div>

          <h1 className="font-display text-[2.6rem] font-extrabold leading-[1.03] tracking-tight text-[var(--ob-ink)] sm:text-6xl lg:text-[4.4rem]">
            Email &amp; SMS that adds{' '}
            <span className="text-[var(--ob-accent)]">36%+</span> to your bottom line.
          </h1>

          <p className="mt-6 max-w-md text-lg leading-relaxed text-[var(--ob-ink-soft)]">
            If you’re a Shopify brand, we’re a team with over 10 years of experience that
            will scale your retention. <span className="font-semibold text-[var(--ob-ink)]">Profitably.</span>
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <a
              href="https://calendly.com/hello-onbrandadmin/30min" target="_blank" rel="noopener noreferrer"
              className="ob-btn group inline-flex items-center justify-center gap-2 rounded-full bg-[var(--ob-accent)] px-8 py-3 text-lg font-bold text-[var(--ob-dark)] shadow-[0_14px_30px_-12px_rgba(143,191,63,0.8)] hover:bg-[var(--ob-accent-dark)] hover:text-white"
            >
              <span className="flex flex-col items-center text-center leading-[1.15]">
                <span>Get a Free</span>
                <span>Strategy Audit</span>
              </span>
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#results"
              className="ob-btn inline-flex items-center justify-center whitespace-nowrap rounded-full border border-[var(--ob-ink)] px-8 py-3 text-lg font-semibold text-[var(--ob-ink)] hover:bg-[var(--ob-ink)] hover:text-white"
            >
              See The Results
            </a>
          </div>

          <p className="mt-6 text-sm text-[var(--ob-ink-soft)]">
            Trusted by 8- to 9-figure Shopify brands · $55M+ generated in 2025
          </p>

          {/* Compact Trustpilot rating (genuine figures; not a live widget) */}
          <a
            href={TRUSTPILOT.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-trustpilot-rating"
            aria-label={`Rated ${TRUSTPILOT.score} out of 5, ${TRUSTPILOT.label}, on Trustpilot. Opens in a new tab.`}
            className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-[var(--ob-line)] bg-white px-4 py-2 text-sm shadow-sm transition-all duration-200 hover:border-[var(--ob-ink)] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ob-accent)]"
          >
            <HeroTrustStars value={TRUSTPILOT.scoreValue} size={15} />
            <span className="font-display font-bold text-[var(--ob-ink)]">
              {TRUSTPILOT.score}/5
            </span>
            <span className="text-[var(--ob-ink-soft)]">· {TRUSTPILOT.label} on</span>
            {TRUSTPILOT.logo ? (
              <img src={TRUSTPILOT.logo} alt="Trustpilot" className="h-4 w-auto" draggable={false} />
            ) : (
              <span className="font-semibold text-[var(--ob-ink-soft)]">Trustpilot</span>
            )}
          </a>
        </div>

        {/* Right: animated email columns (desktop). Emails stay sharp; only the top/bottom
            boundaries soften into the background via localized gradient overlays. */}
        <div className="relative hidden md:block">
          <div className="grid h-screen grid-cols-3 gap-4 py-6">
            <div><EmailColumn items={colA} direction="up" duration={46} /></div>
            <div><EmailColumn items={colB} direction="down" duration={54} /></div>
            <div><EmailColumn items={colC} direction="up" duration={50} /></div>
          </div>
          <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[var(--ob-bg)] via-[var(--ob-bg)]/80 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[var(--ob-bg)] via-[var(--ob-bg)]/80 to-transparent" />
        </div>

        {/* Right: horizontal marquee (mobile). Fixed-height window per card gives every
            email a consistent viewport; images keep their aspect ratio (w-full h-auto),
            start at the top and are clipped by overflow-hidden. Every source email is
            taller than this window at 192px width, so no card shows blank white space. */}
        <div className="relative -mx-5 mb-6 block overflow-hidden md:hidden">
          <div className="ob-fade-x">
            <div className="ob-marquee ob-marquee--left" style={{ animationDuration: '40s' }}>
              {[...EMAIL_DESIGNS, ...EMAIL_DESIGNS].map((e, i) => (
                <div key={i} className="mx-2 h-[420px] w-48 shrink-0 overflow-hidden rounded-xl bg-white shadow-lg">
                  <img
                    src={e.src}
                    alt={e.alt}
                    loading="lazy"
                    draggable={false}
                    className="block h-auto w-full object-top"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
